import type {
  InteractionHandlerName,
  MapClickEvent,
  MapControllerConfig,
  MapInitialized,
  MapInstance,
  MapRendererApp,
  RenderedMapFeature,
  ToggleableMapHandler,
} from './types.ts';

type UserInteractionState = {
  enablePoiSelection: boolean;
  debugLocationMode?: boolean;
};

export default function UserInteractionController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  config: MapControllerConfig,
) {
  let state: UserInteractionState = {
    enablePoiSelection: config.enablePoiSelection ?? true,
  };

  mapInitialized().then(map => {
    if (config.enableDoubleClickZoom) {
      map.doubleClickZoom.enable();
    } else {
      map.doubleClickZoom.disable();
    }
    if (config.disableTouchRotation) {
      map.touchZoomRotate.disableRotation();
    } else {
      map.touchZoomRotate.enableRotation();
    }

    map.on('click', async function (e: MapClickEvent) {
      const ord = await app.bus.get<undefined, number | null>('map/getCurrentOrdinal');

      // Shift-click emits a separate event (used by debug location providers).
      // BoxZoom must be disabled first for this to fire (see map/setDebugLocationMode).
      if (e.originalEvent && e.originalEvent.shiftKey) {
        app.bus.send('map/shiftClick', { lat: e.lngLat.lat, lng: e.lngLat.lng, ord });
        return;
      }

      app.bus.send('map/click', { lat: e.lngLat.lat, lng: e.lngLat.lng, ord });

      if (!state.enablePoiSelection) {
        return;
      }
      const queryPoint = e.point as Parameters<MapInstance['queryRenderedFeatures']>[0];
      const features = map.queryRenderedFeatures(queryPoint) as RenderedMapFeature[];
      const entity = features
        ? features.find(f => f.source === 'llNavSource') || // top priority: nav badge
          features.find(f => f.properties?.aiLayer === 'poi') || // second: general poi
          features[0] // else: first one
        : undefined;
      if (entity) {
        if (entity.layer?.source === 'llBuildingLabelsSource') {
          if (entity.properties.terminalPoiLabel) {
            const [lng, lat] = (entity.geometry as GeoJSON.Point).coordinates;
            app.bus.send('map/animateToPoint', {
              lat,
              lng,
              zoom: 18,
            });
          } else {
            app.bus.send('mapLevelSelector/selectBuilding', {
              id: entity.properties.buildingId,
              userInitiated: true,
            });
          }
        } else {
          const poi = await app.bus.get('poi/getById', {
            id: entity.properties.id,
          });
          if (poi) {
            app.bus.send('map/poiClicked', { poi });
          }
          if (entity.properties.category === 'basemap.jetway' && entity.geometry?.coordinates) {
            app.bus.send('map/animateToPoint', {
              lat: entity.geometry.coordinates[1],
              lng: entity.geometry.coordinates[0],
              zoom: 19.5,
            });
          }
        }
      }
    });

    app.bus.on('map/setDebugLocationMode', ({ enabled }: { enabled: boolean }) => {
      state = { ...state, debugLocationMode: enabled };
      if (enabled) {
        map.boxZoom.disable();
      } else {
        map.boxZoom.enable();
      }
    });
  });

  app.bus.on('map/setEnablePoiSelection', payload => {
    // Legacy bare-boolean payload, predating the BusPayload object-literal
    // rule (see legacyBusPayload in bustle.ts). Note the event catalog
    // documents this payload as `{enabled: bool}` — senders following the
    // catalog would not behave as intended here.
    const value = payload as unknown as boolean;
    state = { ...state, enablePoiSelection: value };
  });

  app.bus.on('map/disableMap', ({ disable }: { disable: boolean }) => {
    mapInitialized().then(map => {
      const interactionHandlers: InteractionHandlerName[] = [
        'boxZoom',
        'doubleClickZoom',
        'dragPan',
        'dragRotate',
        'scrollZoom',
        'touchPitch',
        'touchZoomRotate',
      ];
      interactionHandlers.forEach(handler => {
        const mapHandler = map[handler] as ToggleableMapHandler;
        if (disable) {
          mapHandler.disable();
        } else {
          mapHandler.enable();
        }
      });
    });
  });
}
